import { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuthenticatedApi } from './api';
import { useSearchParams } from 'react-router-dom';
import PaymentForm from './PaymentForm';
import { toast } from 'react-hot-toast';

export default function Plans() {
  const [isAnnual, setIsAnnual] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null); // Track selected plan
  const [priceIds, setPriceIds] = useState(null);
  const [loadingPrices, setLoadingPrices] = useState(true);
  const [memberData, setMemberData] = useState(null);
  const [loadingMember, setLoadingMember] = useState(true);
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);
  const checkoutStartedRef = useRef(false);
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth0();
  const { authenticatedFetch } = useAuthenticatedApi();


  // Fetch member data to get current subscription
  useEffect(() => {
    const fetchMemberData = async () => {
      if (!isAuthenticated || !user?.email) {
        setLoadingMember(false);
        return;
      }
      
      try {
        const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL || '/api'}/members/by-email?email=${encodeURIComponent(user.email)}`);
        if (response.ok) {
          const data = await response.json();
          setMemberData(data);
        }
      } catch (err) {
        console.error('Error fetching member data:', err);
      } finally {
        setLoadingMember(false);
      }
    };

    fetchMemberData();
  }, [isAuthenticated, user, authenticatedFetch]);

  // Fetch price IDs from server
  useEffect(() => {
    const fetchPriceIds = async () => {
      if (!isAuthenticated) {
        setLoadingPrices(false);
        return;
      }
      
      try {
        const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL || '/api'}/stripe/config`);
        if (response.ok) {
          const data = await response.json();
          setPriceIds(data.config.currentPrices);
        } else {
          throw new Error('Failed to fetch price IDs');
        }
      } catch (err) {
        console.error('Error fetching price IDs:', err);
        setError('Failed to load subscription plans');
      } finally {
        setLoadingPrices(false);
      }
    };

    fetchPriceIds();
  }, [isAuthenticated, authenticatedFetch]);

  // Handle successful checkout return
  useEffect(() => {
    const handleCheckoutSuccess = async () => {
      if (!isAuthenticated) {
        return;
      }
      
      const success = searchParams.get('success');
      const sessionId = searchParams.get('session_id');
      
      if (success === 'true' && sessionId) {
        try {
          const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL || '/api'}/checkout-success?session_id=${sessionId}`);
          
          if (response.ok) {
            const data = await response.json();
            setError(null);
            // Clear URL parameters
            window.history.replaceState({}, document.title, window.location.pathname);
          } else {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to activate subscription');
          }
        } catch (err) {
          console.error('❌ Error processing checkout success:', err);
          setError(err.message || 'Failed to activate subscription');
        }
      }
    };

    handleCheckoutSuccess();
  }, [isAuthenticated, searchParams, authenticatedFetch]);

  // Preselect billing and plan from URL when present; do not run on checkout return
  useEffect(() => {
    const success = searchParams.get('success');
    const sessionId = searchParams.get('session_id');
    if (success === 'true' && sessionId) return;

    const b = searchParams.get('b');
    if (b === 'annual') setIsAnnual(true);
    else if (b === 'monthly') setIsAnnual(false);

    const p = searchParams.get('p');
    const validPlans = ['Basic', 'Professional', 'Premium'];
    if (p) {
      const plan = p.charAt(0).toUpperCase() + p.slice(1).toLowerCase();
      if (validPlans.includes(plan)) setSelectedPlan(plan);
    }
  }, [searchParams]);

  // For new subscriptions: create checkout session and redirect without showing "Complete your subscription" page
  useEffect(() => {
    if (!selectedPlan || loadingPrices || loadingMember || !isAuthenticated || !user || !priceIds) return;

    const hasExistingSubscription = memberData?.status && ['active', 'subscribed', 'canceling'].includes(memberData.status);
    const currentPlanName = memberData?.subscription ? memberData.subscription.charAt(0).toUpperCase() + memberData.subscription.slice(1) : null;
    const planHierarchy = { 'Basic': 1, 'Professional': 2, 'Premium': 3 };
    const isUpgrade = hasExistingSubscription && currentPlanName && planHierarchy[selectedPlan] > (planHierarchy[currentPlanName] || 0);
    const isDowngrade = hasExistingSubscription && currentPlanName && planHierarchy[selectedPlan] < (planHierarchy[currentPlanName] || 0);

    if (isUpgrade || isDowngrade) return;

    const planKey = selectedPlan.toLowerCase();
    const priceId = isAnnual ? priceIds[planKey]?.annual : priceIds[planKey]?.monthly;
    if (!priceId) return;

    if (checkoutStartedRef.current) return;
    checkoutStartedRef.current = true;
    setIsCreatingCheckout(true);

    (async () => {
      try {
        const requestBody = {
          priceId,
          period: isAnnual ? 'annual' : 'monthly',
          planName: selectedPlan.toLowerCase(),
          customerEmail: user.email,
          customerName: user.name || 'Customer'
        };
        if (memberData?.coupon) requestBody.couponCode = memberData.coupon;

        const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL || '/api'}/create-subscription`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to create checkout session');
        }

        const sessionData = await response.json();
        if (sessionData.upgraded && sessionData.redirectUrl) {
          window.location.href = sessionData.redirectUrl;
          return;
        }
        if (sessionData.url) {
          window.location.href = sessionData.url;
        } else {
          throw new Error('No checkout URL received from server');
        }
      } catch (err) {
        console.error('❌ Checkout session creation error:', err);
        setError(err.message || 'Failed to create checkout session');
        setIsCreatingCheckout(false);
        checkoutStartedRef.current = false;
      }
    })();
  }, [selectedPlan, loadingPrices, loadingMember, isAuthenticated, user, memberData, priceIds, isAnnual, authenticatedFetch]);

  const plans = [
    {
      name: 'Basic',
      monthlyPrice: '$9.99',
      annualPrice: '$99',
      monthlyPriceId: priceIds?.basic?.monthly || '',
      annualPriceId: priceIds?.basic?.annual || '',
      features: [
        {
          category: 'Opportunity',
          items: ['Freelance opportunities and job listings in the Opportunity Finder and newsletter']
        },
        {
          category: 'Knowledge',
          items: ['Unlock weekly newsletters, paywalled content, media criticism, and educational resources']
        },
        {
          category: 'Community',
          items: ['Access to community discussions and industry insights']
        }
      ]
    },
    {
      name: 'Professional',
      monthlyPrice: '$14.99',
      annualPrice: '$149',
      monthlyPriceId: priceIds?.professional?.monthly || '',
      annualPriceId: priceIds?.professional?.annual || '',
      features: [
        {
          category: 'Opportunity',
          items: ['Freelance opportunities and job listings in the Opportunity Finder and newsletter']
        },
        {
          category: 'Knowledge',
          items: ['Unlock weekly newsletters, paywalled content, media criticism, and educational resources']
        },
        {
          category: 'Community',
          items: ['Access to community discussions and industry insights', 'Connect and network with peers across the media industry via the Study Hall Forum']
        }
      ]
    },
    {
      name: 'Premium',
      monthlyPrice: '$19.99',
      annualPrice: '$199',
      monthlyPriceId: priceIds?.premium?.monthly || '',
      annualPriceId: priceIds?.premium?.annual || '',
      features: [
        {
          category: 'Opportunity',
          items: ['Freelance opportunities and job listings in the Opportunity Finder and newsletter']
        },
        {
          category: 'Knowledge',
          items: ['Unlock weekly newsletters, paywalled content, media criticism, and educational resources']
        },
        {
          category: 'Community',
          items: ['Access to community discussions and industry insights', 'Connect and network with peers across the media industry via the Study Hall Forum', 'Slack channel', 'Creative Listserv']
        }
      ]
    }
  ];

  const currentPlans = plans.map(plan => ({
    ...plan,
    price: isAnnual ? plan.annualPrice : plan.monthlyPrice,
    period: isAnnual ? 'year' : 'month',
    savings: isAnnual ? 17 : 0 // Fixed savings calculation
  }));

  const formatPrice = (price, isAnnual) => {
    // Handle string prices like "$99" or "$9.99"
    if (typeof price === 'string') {
      // Remove the "$" and convert to number
      const numericPrice = parseFloat(price.replace('$', ''));
      if (isNaN(numericPrice)) {
        return price; // Return original if parsing fails
      }
      return isAnnual ? Math.floor(numericPrice).toString() : numericPrice.toFixed(2);
    }
    // Handle numeric prices
    return isAnnual ? Math.floor(price).toString() : price.toFixed(2);
  };

  const handlePlanSelection = (planName) => {
    setError(null);
    setSelectedPlan(planName);
  };

  const handleBackToPlans = () => {
    setSelectedPlan(null);
    setError(null);
  };


  const handlePaymentSuccess = () => {
    toast.success('Subscription successful! Welcome to Study Hall!');
    setSelectedPlan(null);
  };

  // Check if user has an existing active subscription
  const hasActiveSubscription = memberData && 
    memberData.status && 
    (memberData.status === 'active' || memberData.status === 'subscribed' || memberData.status === 'canceling');
  
  // Get coupon code from database
  const memberCouponCode = memberData?.coupon;

  // If a plan is selected, show payment form (or loading until prices/member data are ready)
  if (selectedPlan) {
    const selectedPlanData = currentPlans.find(p => p.name === selectedPlan);
    const hasValidPriceIds = selectedPlanData && (selectedPlanData.annualPriceId || selectedPlanData.monthlyPriceId);

    if (loadingPrices || loadingMember || !hasValidPriceIds || isCreatingCheckout) {
      return (
        <div style={{ padding: '2rem' }}>
          <div style={{
            backgroundColor: '#e8f5e8',
            border: '1px solid #38b44a',
            color: '#38b44a',
            padding: '2rem',
            borderRadius: '8px',
            marginBottom: '2rem',
            textAlign: 'center'
          }}>
            <strong>Preparing your checkout...</strong>
          </div>
        </div>
      );
    }
    
    // Determine if this is a new subscription, upgrade, or downgrade
    const hasExistingSubscription = memberData && 
      memberData.status && 
      (memberData.status === 'active' || memberData.status === 'subscribed' || memberData.status === 'canceling');
    
    const currentPlanName = memberData?.subscription ? memberData.subscription.charAt(0).toUpperCase() + memberData.subscription.slice(1) : null;
    
    // Plan hierarchy for comparison
    const planHierarchy = { 'Basic': 1, 'Professional': 2, 'Premium': 3 };
    const isUpgrade = hasExistingSubscription && 
      currentPlanName && 
      planHierarchy[selectedPlan] > (planHierarchy[currentPlanName] || 0);
    const isDowngrade = hasExistingSubscription && 
      currentPlanName && 
      planHierarchy[selectedPlan] < (planHierarchy[currentPlanName] || 0);
    
    // Determine the appropriate heading and message
    let heading = 'Complete Your Subscription';
    let subMessage = `You selected: <strong>${selectedPlan}</strong> - $${formatPrice(selectedPlanData.price, isAnnual)}/${selectedPlanData.period}`;
    
    if (isUpgrade) {
      heading = 'Upgrade Your Subscription';
      subMessage = `Upgrading from <strong>${currentPlanName}</strong> to <strong>${selectedPlan}</strong> - $${formatPrice(selectedPlanData.price, isAnnual)}/${selectedPlanData.period}`;
    } else if (isDowngrade) {
      heading = 'Change Your Subscription';
      subMessage = `Changing from <strong>${currentPlanName}</strong> to <strong>${selectedPlan}</strong> - $${formatPrice(selectedPlanData.price, isAnnual)}/${selectedPlanData.period}`;
    }
    
    return (
      <div style={{ padding: '2rem' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <button 
            onClick={handleBackToPlans}
            style={{
              position: 'absolute',
              left: '2rem',
              top: '6rem',
              backgroundColor: 'transparent',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: '8px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
              transition: 'background-color 0.2s',
              zIndex: 10
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f0f0f0';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
          >
            ←
          </button>
          <h2>{heading}</h2>
          <p style={{ color: '#666', fontSize: '18px' }} dangerouslySetInnerHTML={{ __html: subMessage }} />
        </div>

        {error && (
          <div style={{
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            color: '#c33',
            padding: '1rem',
            borderRadius: '6px',
            marginBottom: '2rem',
            textAlign: 'center'
          }}>
            <strong>Error:</strong> {error}
          </div>
        )}

             {/* Payment Form */}
             <PaymentForm
               selectedPlan={selectedPlan}
               selectedPlanData={selectedPlanData}
               isAnnual={isAnnual}
               user={user}
               authenticatedFetch={authenticatedFetch}
               onSuccess={handlePaymentSuccess}
               onError={setError}
               isUpgrade={isUpgrade}
               isDowngrade={isDowngrade}
               couponCode={memberCouponCode}
             />
      </div>
    );
  }

  // Show plan selection
  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <h2>SELECT A SUBSCRIPTION</h2>
        {hasActiveSubscription && (
          <p style={{ color: '#666', fontSize: '14px' }}>
            {isAnnual ? 'Save 17% with annual billing' : 'Monthly billing'}
          </p>
        )}
        {!hasActiveSubscription && !memberCouponCode && (
          <div style={{
            backgroundColor: '#e8f5e8',
            border: '1px solid #38b44a',
            borderRadius: '8px',
            padding: '1rem',
            marginTop: '1rem',
            maxWidth: '600px',
            margin: '1rem auto 0'
          }}>
            <p style={{ 
              color: '#2d8f3a', 
              fontSize: '16px', 
              fontWeight: '600',
              margin: 0
            }}>
              🎉 Start your 7-day free trial today!
            </p>
            <p style={{ 
              color: '#666', 
              fontSize: '14px', 
              margin: '0.5rem 0 0 0',
              lineHeight: '1.5'
            }}>
              No charges for 7 days. Cancel anytime during your trial.
            </p>
          </div>
        )}
        {!hasActiveSubscription && memberCouponCode && (
          <div style={{
            backgroundColor: '#e8f5e8',
            border: '1px solid #38b44a',
            borderRadius: '8px',
            padding: '1rem',
            marginTop: '1rem',
            maxWidth: '600px',
            margin: '1rem auto 0'
          }}>
            <p style={{ 
              color: '#2d8f3a', 
              fontSize: '16px', 
              fontWeight: '600',
              margin: 0
            }}>
              Coupon {memberCouponCode} will be applied
            </p>
          </div>
        )}
      </div>

      {error && (
        <div style={{
          backgroundColor: '#fee',
          border: '1px solid #fcc',
          color: '#c33',
          padding: '1rem',
          borderRadius: '6px',
          marginBottom: '2rem',
          textAlign: 'center'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {(loadingPrices || loadingMember) && (
        <div style={{
          backgroundColor: '#e8f5e8',
          border: '1px solid #38b44a',
          color: '#38b44a',
          padding: '1rem',
          borderRadius: '6px',
          marginBottom: '2rem',
          textAlign: 'center'
        }}>
          <strong>Loading subscription plans...</strong>
        </div>
      )}
      
      {/* Billing Toggle */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: '1rem', 
        marginBottom: '2rem',
        padding: '1rem',
        backgroundColor: 'white',
        borderRadius: '8px',
        border: '1px solid rgb(204, 204, 204)',
        boxShadow: 'rgba(0, 0, 0, 0.1) 0px 2px 8px'
      }}>
        <span style={{ color: !isAnnual ? '#000' : '#666', fontWeight: !isAnnual ? 'bold' : 'normal' }}>Monthly</span>
        <button
          onClick={() => setIsAnnual(!isAnnual)}
          style={{
            width: '60px',
            height: '30px',
            backgroundColor: isAnnual ? '#38b44a' : '#ccc',
            border: 'none',
            borderRadius: '15px',
            position: 'relative',
            cursor: 'pointer',
            transition: 'background-color 0.3s'
          }}
        >
          <div style={{
            width: '26px',
            height: '26px',
            backgroundColor: 'white',
            borderRadius: '50%',
            position: 'absolute',
            top: '2px',
            left: isAnnual ? '32px' : '2px',
            transition: 'left 0.3s'
          }} />
        </button>
        <span style={{ color: isAnnual ? '#000' : '#666', fontWeight: isAnnual ? 'bold' : 'normal' }}>
          Annual
          <span style={{ color: '#38b44a', marginLeft: '0.5rem' }}>(Save 17%)</span>
        </span>
      </div>

      {/* Plans Grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
        gap: '1.5rem', 
        marginTop: '1rem' 
      }}>
        {currentPlans.map((plan) => (
          <div key={plan.name} style={{ 
            border: plan.name === 'Professional' ? '2px solid #38b44a' : '1px solid #ccc', 
            borderRadius: '12px', 
            padding: '3rem 2rem 2rem 2rem',
            position: 'relative',
            backgroundColor: 'white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            {plan.name === 'Professional' ? (
              <div style={{
                position: 'absolute',
                top: '-10px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: '#38b44a',
                color: 'white',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '0.8rem',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <span>⭐</span>
                Most Popular
              </div>
            ) : plan.savings > 0 && (
              <div style={{
                position: 'absolute',
                top: '-10px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: '#38b44a',
                color: 'white',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '0.8rem',
                fontWeight: 'bold'
              }}>
                Save {plan.savings}%
              </div>
            )}
            
            <h3 style={{ marginTop: 0, marginBottom: '0.5rem', textAlign: 'center' }}>{plan.name}</h3>
            <div style={{ marginBottom: '1rem', textAlign: 'center' }}>
              <span style={{ fontSize: '2rem', fontWeight: 'bold' }}>${formatPrice(plan.price, isAnnual)}</span>
              <span style={{ color: '#666' }}>/{plan.period}</span>
            </div>
            
            {/* Features */}
            <div style={{ marginTop: '1rem' }}>
              {plan.features.map((feature, index) => (
                <div key={`${plan.name}-${feature.category}`} style={{ marginBottom: '1rem' }}>
                  <h4 style={{ 
                    color: '#38b44a', 
                    marginBottom: '0.5rem',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}>
                    {feature.category}
                  </h4>
                  <ul style={{ 
                    margin: 0, 
                    paddingLeft: '1.5rem',
                    fontSize: '13px',
                    color: '#666'
                  }}>
                    {feature.items.map((item, itemIndex) => (
                      <li key={`${plan.name}-${feature.category}-${itemIndex}`} style={{ marginBottom: '0.25rem' }}>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            
            {(() => {
              // Check if this is the user's current plan
              const isCurrentPlan = memberData && 
                memberData.status && 
                (memberData.status === 'active' || memberData.status === 'subscribed' || memberData.status === 'canceling') &&
                memberData.subscription && 
                memberData.subscription.toLowerCase() === plan.name.toLowerCase() &&
                memberData.period &&
                memberData.period.toLowerCase() === (isAnnual ? 'annual' : 'monthly');
              
              if (isCurrentPlan) {
                return (
                  <button 
                    disabled
                    style={{
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '6px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'not-allowed',
                      width: '100%',
                      marginTop: '1rem',
                      opacity: 0.7
                    }}
                  >
                    Current Plan
                  </button>
                );
              } else {
                return (
                  <button 
                    onClick={() => handlePlanSelection(plan.name)}
                    style={{
                      backgroundColor: '#38b44a',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '6px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      width: '100%',
                      marginTop: '1rem'
                    }}
                  >
                    Select {plan.name}
                  </button>
                );
              }
            })()}
          </div>
        ))}
      </div>
    </div>
  );
}

